import './Lib/init';
import { useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import loadable from '@loadable/component';

import 'react-datepicker/dist/react-datepicker.css';
import BareLayout from './Layout/BareLayout';
import MainLayout from './Layout/MainLayout/MainLayout';
import SignoutLayout from './Layout/SignoutLayout';
import PrivateRoute from './Lib/PrivateRoute';
import RedirectRoute from './Lib/RedirectRoute';
import PublicRoute from './Lib/PublicRoute';
import UserSignOut from './Components/Auth/UserSignOut';
import UserSignUp from './Components/Onboarding/Pages/SignUp/SignUpPage';
import ResetPassword from 'Components/Auth/ResetPassword';
import UserAct from './Components/Auth/UserActivation';
import UserChangePwd from './Components/Auth/UserChangePwd';
import SwitchForm from './Components/SignIn/SwitchForm';
import NotFoundRoute from 'Components/NotFound';
import Breadcrumb from 'Layout/Breadcrumb';
import awsExports from './aws-exports';
import AppUrlListener from './mobile-app/AppUrlListener';
import { getCompanyFromQueryParams } from 'Lib/Utils';
import SignIn from './Components/SignIn';
import { cardsHomeUrl } from 'Components/CardsGPS/utils/constants';
import CardsLockScreen from 'Components/LockScreens/CardsLockScreen';
import InvitedUserSetup from 'Components/Setup/User/InvitedUserSetup';
import NewUserDownloadAppPage from 'Components/Setup/User/NewUserDownloadAppPage';
import NewUserSetupMFA from 'Components/Setup/User/NewUserSetupMFA';
import RootErrorBoundary from 'Components/RootErrorBoundary';
import { initialiseLogger } from 'utils/logger';
import { isOktaExternalAuthToken, isOktaInternalAuthToken } from 'Lib/Utils/authToken';

// TOP LEVEL PAGES
const Dashboard = loadable(() => import('./Pages/Dashboard'));
const Team = loadable(() => import('Pages/Team/Team'));
const ReferralManager = loadable(() => import('Pages/Referrals/ReferralManager'));
const SearchCompany = loadable(() => import('Components/SearchCompany'));

const AccountsV2 = loadable(() => import('./Components/AccountsV2/Pages/Accounts'));
const MobileMainMenu = loadable(() => import('./Components/Menu/MobileMenu'));
const OnboardingWrap = loadable(() => import('./Components/Onboarding/Pages/Onboarding/OnboardingWrap'));
const CollectionLink = loadable(() => import('./Components/Onboarding/Pages/CollectionLink'));
const MyAccount = loadable(() => import('./Components/Setup'));
const ReferralAction = loadable(() => import('Pages/Referrals'), {
    resolveComponent: (components) => components.ReferralAction,
});
const MissingReferralAction = loadable(() => import('Pages/Referrals'), {
    resolveComponent: (components) => components.MissingReferralAction,
});
const Payments = loadable(() => import('./Components/Payments'));
const Batch = loadable(() => import('./Components/Payments/BatchUpload/index'));
const FileStatus = loadable(() => import('./Components/Payments/SummariseGroupOfPayments/FileStatus/index'));
const Beneficiaries = loadable(() => import('./Components/Beneficiaries/Beneficiaries'));

const XeroOAuth = loadable(() => import('./Components/Integrations/Xero/XeroOAuth'));
const CreateCompany = loadable(() => import('Components/Onboarding/Components/CreateCompany'));
const AccountOverview = loadable(() => import('Components/AccountOverview'));
const Payroll = loadable(() => import('Components/Payroll'));
const WelcomePage = loadable(() => import('Components/Onboarding/Pages/Welcome'));

const AuthConnectSignIn = loadable(() => import('Components/AuthConnect/AuthConnectSignIn'));
const AuthConnectSelect = loadable(() => import('Components/AuthConnect/AuthConnectSelect'));

const OktaExternalSignInRedirect = loadable(() => import('Components/Auth/OktaExternal/OktaExternalSignInRedirect'));
const OktaExternalSignOutRedirect = loadable(() => import('Components/Auth/OktaExternal/OktaExternalSignOutRedirect'));

const OktaInternalAutoSignInRedirect = loadable(() => import('Components/Auth/OktaInternal/OktaInternalAutoSignIn'));
const OktaInternalSignInRedirect = loadable(() => import('Components/Auth/OktaInternal/OktaInternalSignInRedirect'));
const OktaInternalSignOutRedirect = loadable(() => import('Components/Auth/OktaInternal/OktaInternalSignOutRedirect'));

const CardsGPS = loadable(() => import('./Components/CardsGPS/Pages/Cards'));
const Cardholder = loadable(() => import('./Components/CardsGPS/Pages/Cardholder'));

const PartialPayments = loadable(() => import('Components/Payments/Partial'), {
    resolveComponent: (components) => components.PartialPayments,
});
const CardsExpenses = loadable(() => import('Components/Expenses/CardsExpenses'));
const NewUserPage = loadable(() => import('Components/Setup/User'));
const Billing = loadable(() => import('Components/Setup/Billing/Pages/Billing'));
const BatchInvite = loadable(() => import('Pages/Team/Invite/BatchInvite'));
const Integrations = loadable(() => import('Components/Integrations'));
const Approvals = loadable(() => import('Components/Approvals'));
const Policies = loadable(() => import('Components/Approvals/Policies'));
const BatchLockScreen = loadable(() => import('Components/LockScreens/BatchLockScreen'));
const PayrollLockScreen = loadable(() => import('Components/LockScreens/PayrollLockScreen'));
const PartialPaymentsLockScreen = loadable(() => import('Components/LockScreens/PartialPaymentsLockScreen'));
const PaymentsLockScreen = loadable(() => import('Components/LockScreens/PaymentsLockScreen'));
const AccountsLockScreen = loadable(() => import('Components/LockScreens/AccountsLockScreen'));
const PinCallback = loadable(() => import('Components/FundByCard/PinCallback'));
const KYC = loadable(() => import('Components/Setup/KYCandKYB/KYC'));
const KYB = loadable(() => import('Components/Setup/KYCandKYB/KYB'));
const BatchOnboard = loadable(() => import('Components/Onboarding/Pages/BatchOnboarding/BatchOnboarding'));
const Budgets = loadable(() => import('Components/Budgets'));
const BudgetOverviewV2 = loadable(() => import('Components/Budgets/BudgetOverviewV2'));
const Bills = loadable(() => import('Components/Bills/Pages/Bills'));
const CardHolderPageTitle = loadable(() => import('Components/CardsGPS/utils/CardHolderPageTitle'));
const ActivateUser = loadable(() => import('Components/ActivateUser/ActivateUser'));
const VerifyEmail = loadable(() => import('Components/Auth/VerifyEmail/VerifyEmail'));

initialiseLogger();

if (
    !isOktaInternalAuthToken() &&
    !isOktaExternalAuthToken() &&
    Object.keys(localStorage).find((element) => element.startsWith('CognitoIdentityServiceProvider'))
) {
    Amplify.configure(awsExports);
}
const App = () => {
    useLayoutEffect(() => {
        getCompanyFromQueryParams();
    }, []);

    return (
        <Router basename="/">
            <RootErrorBoundary>
                <AppUrlListener />
                <Routes>
                    <Route
                        path="/signin"
                        element={<PublicRoute component={SignIn} layout={BareLayout} name="signin" />}
                    />
                    <Route path="/signout" element={<PublicRoute component={UserSignOut} layout={SignoutLayout} />} />
                    <Route
                        path="/signup"
                        element={<PublicRoute component={UserSignUp} layout={BareLayout} name="signup" />}
                    />
                    <Route
                        path="/activate-user"
                        element={<PublicRoute component={ActivateUser} layout={BareLayout} />}
                    />
                    <Route path="/forgot-pwd" element={<PublicRoute component={ResetPassword} layout={BareLayout} />} />
                    <Route path="/reset-pwd" element={<PublicRoute component={ResetPassword} layout={BareLayout} />} />
                    <Route path="/update-pwd" element={<PublicRoute component={ResetPassword} layout={BareLayout} />} />
                    <Route path="/verify-email" element={<PublicRoute component={UserAct} layout={BareLayout} />} />
                    <Route
                        path="/verify-email-v2"
                        element={<PublicRoute component={VerifyEmail} layout={BareLayout} />}
                    />
                    <Route path="/switch" element={<PrivateRoute component={SwitchForm} layout={BareLayout} />} />
                    <Route
                        path="/home"
                        element={<PrivateRoute component={SearchCompany} layout={MainLayout} oktaInternalOnly />}
                    />
                    <Route
                        path="/user-setup"
                        element={<PrivateRoute component={InvitedUserSetup} layout={BareLayout} />}
                    />
                    <Route
                        path="/download-app"
                        element={<PrivateRoute component={NewUserDownloadAppPage} layout={BareLayout} />}
                    />
                    <Route
                        path="/setup-2fa"
                        element={<PrivateRoute component={NewUserSetupMFA} layout={BareLayout} />}
                    />
                    <Route
                        path="/actius"
                        element={<Navigate replace to="/referrals/5f533eb5-1b25-4659-902c-4561a3157180" />}
                    />
                    <Route path="/referrals/employmenthero" element={<Navigate replace to="/referrals/keypay" />} />
                    <Route
                        path="/referrals/:code"
                        element={<PublicRoute component={ReferralAction} layout={BareLayout} />}
                    />
                    <Route
                        path="/referrals"
                        element={<PublicRoute component={MissingReferralAction} layout={BareLayout} />}
                    />
                    <Route path="/kyc/:code" element={<PublicRoute component={CollectionLink} layout={BareLayout} />} />
                    <Route path="/xero" element={<PublicRoute component={XeroOAuth} layout={BareLayout} />} />
                    <Route
                        path="/"
                        element={<PrivateRoute title="Dashboard" component={Dashboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/connect/select"
                        element={
                            <PrivateRoute title="Select Company" component={AuthConnectSelect} layout={BareLayout} />
                        }
                    />
                    <Route
                        path="/connect"
                        element={<PublicRoute component={AuthConnectSignIn} layout={BareLayout} />}
                    />
                    <Route
                        path="/approvals/policies/:tab/:resourceType/:policyId"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Approvals"
                                title={<Breadcrumb parent="Approvals" parentPath="/approvals" child="Policies" />}
                                component={Policies}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/approvals/policies/:tab"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Approvals"
                                title={<Breadcrumb parent="Approvals" parentPath="/approvals" child="Policies" />}
                                component={Policies}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/approvals/policies"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Approvals"
                                title={<Breadcrumb parent="Approvals" parentPath="/approvals" child="Policies" />}
                                component={Policies}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/approvals/policies/:tab/new"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Approvals"
                                title={<Breadcrumb parent="Approvals" parentPath="/approvals" child="Policies" />}
                                component={Policies}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/approvals/:tab/payment/:id/"
                        element={<PrivateRoute title="Approvals" component={Approvals} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/approvals/:tab/expense/:id/:transactionId"
                        element={<PrivateRoute title="Approvals" component={Approvals} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/approvals/:tab/budget/:id/"
                        element={<PrivateRoute title="Approvals" component={Approvals} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/approvals/:tab"
                        element={<PrivateRoute title="Approvals" component={Approvals} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/approvals"
                        element={<PrivateRoute title="Approvals" component={Approvals} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/overview"
                        element={<PrivateRoute title="Overview" component={AccountOverview} layout={MainLayout} />}
                    />
                    <Route
                        path="/"
                        element={<PrivateRoute title="Dashboard" component={Dashboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/dashboard/deposit/:method"
                        element={<PrivateRoute title="Dashboard" component={Dashboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/dashboard/deposit"
                        element={<PrivateRoute title="Dashboard" component={Dashboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/dashboard"
                        element={<PrivateRoute title="Dashboard" component={Dashboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/menu"
                        element={<PrivateRoute title="Menu" component={MobileMainMenu} layout={MainLayout} />}
                    />
                    <Route
                        path="/accounts/convert/:sell/:buy"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Convert funds"
                                title={<Breadcrumb parent="Accounts" parentPath="/accounts" child="Convert" />}
                                component={AccountsV2}
                                layout={MainLayout}
                                lock={AccountsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/accounts/transfer"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/deposit"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/add"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/statement"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/upcoming"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/search"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/*"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/expense/:expenseId"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/payment/:paymentId"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/account/:accountId/*"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/account/:accountId/convert/:sell/:buy"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/account/:accountId/expense/:expenseId"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/account/:accountId/payment/:paymentId"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/account/:accountId/deposit/:method"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/accounts/deposit/:method"
                        element={<PrivateRoute title="Accounts" component={AccountsV2} layout={MainLayout} />}
                    />

                    <Route
                        path="/bills"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/upload"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/pay/bulk"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/export"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/edit/bulk"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/view/:id/new-beneficiary"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/create/new-beneficiary"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/view/:id"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/bills/:tab/create"
                        element={<PrivateRoute title="Bills" component={Bills} layout={MainLayout} fullWidth />}
                    />
                    <Route
                        path="/refer"
                        element={<PrivateRoute title="Referrals" component={ReferralManager} layout={MainLayout} />}
                    />
                    <Route path="/payroll" element={<Navigate to="/payments/payroll" />} />
                    <Route
                        path="/payments/payroll"
                        element={
                            <PrivateRoute
                                title="Payroll"
                                component={Payroll}
                                layout={MainLayout}
                                lock={PayrollLockScreen}
                            />
                        }
                    />
                    <Route
                        path="/payments/partial/:id"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Bill Details"
                                title={
                                    <Breadcrumb
                                        parent="Partial payment"
                                        parentPath="/payments/partial"
                                        child="Details"
                                    />
                                }
                                component={PartialPayments}
                                layout={MainLayout}
                                lock={PartialPaymentsLockScreen}
                            />
                        }
                    />
                    <Route
                        path="/payments/partial"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Partial"
                                title={<Breadcrumb parent="Payments" parentPath="/payments/partial" child="Partial" />}
                                component={PartialPayments}
                                layout={MainLayout}
                                lock={PartialPaymentsLockScreen}
                            />
                        }
                    />
                    <Route
                        path="/payments/batch/file/:id"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Batch payments - File"
                                title={<Breadcrumb parent="Payments" parentPath="/payments" child="Batch File" />}
                                component={FileStatus}
                                layout={MainLayout}
                                lock={BatchLockScreen}
                            />
                        }
                    />
                    <Route
                        path="/payments/batch"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Batch payments"
                                title={<Breadcrumb parent="Payments" parentPath="/payments" child="Batch" />}
                                component={Batch}
                                layout={MainLayout}
                                lock={BatchLockScreen}
                            />
                        }
                    />
                    <Route
                        path="/recipients"
                        element={<PrivateRoute title="Recipients" component={Beneficiaries} layout={MainLayout} />}
                    />
                    <Route
                        path="/recipients/new"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Recipients"
                                title={<Breadcrumb parent="Recipients" parentPath="/recipients" child="New" />}
                                component={Beneficiaries}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/recipients/:beneficiaryId"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Recipients"
                                title={<Breadcrumb parent="Recipients" parentPath="/recipients" child="View" />}
                                component={Beneficiaries}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/recipients/:beneficiaryId/edit"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Recipients"
                                title={<Breadcrumb parent="Recipients" parentPath="/recipients" child="Edit" />}
                                component={Beneficiaries}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/payments/pay/:id"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/payments/view/:id"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/payments/new/:id"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/payments/new"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/payments/upload"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route path="/payments" element={<RedirectRoute to="/payments/all" />} />
                    <Route
                        path="/payments/:tab"
                        element={
                            <PrivateRoute
                                title="Payments"
                                component={Payments}
                                layout={MainLayout}
                                lock={PaymentsLockScreen}
                                fullWidth
                            />
                        }
                    />
                    <Route path="/expenses" element={<Navigate to="/expenses/open" />} />
                    <Route
                        path="/expenses/:tab/export"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Expense export"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    {/* EXPENSES */}
                    <Route
                        path="/expenses/open/new"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Upload documents"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    <Route path="/expenses/closed/new" element={<Navigate to="/expenses/open/new" />} />
                    <Route
                        path="/expenses/:tab/batch-updates"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Expense categories"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/expenses/:tab/categories"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Expense categories"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/expenses/:tab/:id/:transactionId"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Expense"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    <Route
                        path="/expenses/:tab"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Expenses"
                                title="Expenses"
                                component={CardsExpenses}
                                layout={MainLayout}
                                fullWidth
                            />
                        }
                    />
                    {/* THREDD CARDS */}
                    <Route path={cardsHomeUrl} element={<RedirectRoute to={`${cardsHomeUrl}/all`} />} />
                    <Route
                        path={`${cardsHomeUrl}/upgrade/subscription-cards`}
                        element={
                            <PrivateRoute
                                title="Cards"
                                component={CardsGPS}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />

                    <Route
                        path={`${cardsHomeUrl}/:tab/new`}
                        element={
                            <PrivateRoute
                                title="Cards"
                                component={CardsGPS}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/:tab`}
                        element={
                            <PrivateRoute
                                title="Cards"
                                component={CardsGPS}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/:tab/history/:cardId`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card history"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/:tab`}
                                        child="Card history"
                                    />
                                }
                                component={CardsGPS}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/:tab/approval/:id`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card approval"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/:tab`}
                                        child="Card approval"
                                    />
                                }
                                component={CardsGPS}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId`}
                        element={
                            <PrivateRoute
                                ignorePageTitle
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child={<CardHolderPageTitle />}
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId`}
                        element={
                            <PrivateRoute
                                ignorePageTitle
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child={<CardHolderPageTitle />}
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/statement`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card statement"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child="Card statement"
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/wallet`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card"
                                title={<Breadcrumb parent="Cards" parentPath={`${cardsHomeUrl}/all`} child="Card" />}
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/cancel`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Cancel card"
                                title={
                                    <Breadcrumb parent="Cards" parentPath={`${cardsHomeUrl}/all`} child="Cancel card" />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/history`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card history"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child="Card history"
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/pin`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card PIN"
                                title={
                                    <Breadcrumb parent="Cards" parentPath={`${cardsHomeUrl}/all`} child="Card PIN" />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/edit`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Edit card"
                                title={
                                    <Breadcrumb parent="Cards" parentPath={`${cardsHomeUrl}/all`} child="Edit card" />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/transaction/:transactionId`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Card transaction"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child="Card transaction"
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path={`${cardsHomeUrl}/user/:userId/card/:cardId/activate`}
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Activate your physical card"
                                title={
                                    <Breadcrumb
                                        parent="Cards"
                                        parentPath={`${cardsHomeUrl}/all`}
                                        child="Activate Card"
                                    />
                                }
                                component={Cardholder}
                                layout={MainLayout}
                                lock={CardsLockScreen}
                                cardsOnly
                            />
                        }
                    />
                    <Route
                        path="/setup/account/kyc/*"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Documentation"
                                title={<Breadcrumb parent="Setup" parentPath="/setup/account" child="Documentation" />}
                                component={KYC}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/account/supporting-info"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Documentation"
                                title={
                                    <Breadcrumb parent="Setup" parentPath="/setup/account" child="Supporting info" />
                                }
                                component={KYB}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/billing/payment-method"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Billing payment method"
                                title={<Breadcrumb parent="Setup" parentPath="/setup/billing" child="Billing" />}
                                component={Billing}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/billing/change-plan"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Billing change plan"
                                title={<Breadcrumb parent="Setup" parentPath="/setup/billing" child="Billing" />}
                                component={Billing}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/billing/unsubscribe"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Billing unsubscribe"
                                title={<Breadcrumb parent="Setup" parentPath="/setup/billing" child="Billing" />}
                                component={Billing}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/billing/*"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Billing details"
                                title={<Breadcrumb parent="Setup" parentPath="/setup" child="Billing" />}
                                component={Billing}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/user/*"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="User setup"
                                title={<Breadcrumb parent="Setup" parentPath="/setup" child="User" />}
                                component={NewUserPage}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/account/*"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Account setup"
                                title={<Breadcrumb parent="Setup" parentPath="/setup" child="Account" />}
                                component={MyAccount}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/integrations"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Integrations"
                                title={<Breadcrumb parent="Setup" parentPath="/setup" child="Integrations" />}
                                component={Integrations}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/setup/integrations/xero/settings"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Integrations"
                                title={<Breadcrumb parent="Setup" parentPath="/setup" child="Integrations" />}
                                component={Integrations}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route path="/welcome" element={<PrivateRoute component={WelcomePage} layout={BareLayout} />} />
                    <Route
                        path="/onboarding/batch"
                        element={<PrivateRoute title="Batch Onboarding" component={BatchOnboard} layout={MainLayout} />}
                    />
                    <Route
                        path="/onboarding/docs/*"
                        element={<PrivateRoute component={OnboardingWrap} layout={BareLayout} />}
                    />
                    <Route
                        path="/onboarding/*"
                        element={<PrivateRoute component={OnboardingWrap} layout={BareLayout} />}
                    />
                    <Route
                        path="/account/change-pwd"
                        element={<PrivateRoute title="Change password" component={UserChangePwd} layout={MainLayout} />}
                    />
                    {/* Okta External */}
                    <Route
                        path="/oktaextsignin"
                        element={<PublicRoute component={OktaExternalSignInRedirect} layout={BareLayout} />}
                    />
                    <Route
                        path="/oktaextsignout"
                        element={<PublicRoute component={OktaExternalSignOutRedirect} layout={BareLayout} />}
                    />
                    {/* Okta Internal */}
                    <Route
                        path="/fenixsignin"
                        element={<PublicRoute component={OktaInternalAutoSignInRedirect} layout={BareLayout} />}
                    />
                    <Route
                        path="/oktaintsignin"
                        element={<PublicRoute component={OktaInternalSignInRedirect} layout={BareLayout} />}
                    />
                    <Route
                        path="/oktaintsignout"
                        element={<PublicRoute component={OktaInternalSignOutRedirect} layout={BareLayout} />}
                    />
                    <Route
                        path="/pin/callback"
                        element={<PrivateRoute title="Loading new card" component={PinCallback} layout={MainLayout} />}
                    />
                    <Route
                        path="/create"
                        element={
                            <PrivateRoute title="Create new company" component={CreateCompany} layout={MainLayout} />
                        }
                    />
                    <Route
                        path="/team/invite/batch"
                        element={
                            <PrivateRoute
                                breadcrumbTitle="Batch Invite"
                                title={<Breadcrumb parent="Team" parentPath="/team" child="Batch Invite" />}
                                component={BatchInvite}
                                layout={MainLayout}
                            />
                        }
                    />
                    <Route
                        path="/team/invite"
                        element={<PrivateRoute title="Team" component={Team} layout={MainLayout} />}
                    />
                    <Route
                        path="/team/user/*"
                        element={<PrivateRoute title="Team" component={Team} layout={MainLayout} />}
                    />
                    <Route
                        path="/team/user/:userId"
                        element={<PrivateRoute title="Team" component={Team} layout={MainLayout} />}
                    />
                    <Route path="/team" element={<PrivateRoute title="Team" component={Team} layout={MainLayout} />} />
                    {/* BUDGETS */}
                    <Route path="/budgets" element={<RedirectRoute to="/budgets/all" />} />
                    <Route
                        path="/budgets/:tab"
                        element={<PrivateRoute title="Budgets" component={Budgets} layout={MainLayout} />}
                    />
                    <Route
                        path="/budgets/:tab/new"
                        element={
                            <PrivateRoute
                                component={Budgets}
                                layout={MainLayout}
                                breadcrumbTitle="Budgets"
                                title={
                                    <Breadcrumb parent="Budgets" parentPath="/budgets/all" child="Create a budget" />
                                }
                            />
                        }
                    />
                    <Route
                        path="/budgets/:tab/:id"
                        element={
                            <PrivateRoute
                                component={Budgets}
                                layout={MainLayout}
                                breadcrumbTitle="Budgets"
                                title={<Breadcrumb parent="Budgets" parentPath="/budgets/all" child="Budget" />}
                            />
                        }
                    />

                    {[
                        '/:id/:tab',
                        '/:id/:tab/edit',
                        '/:id/:tab/:userId',
                        '/:id/:tab/:cardId/history',
                        '/:id/:tab/expense/:expenseId',
                        '/:id/:tab/payment/:paymentId',
                    ].map((path) => (
                        <Route
                            key={path}
                            path={`/budgets/budget${path}`}
                            element={
                                <PrivateRoute
                                    component={BudgetOverviewV2}
                                    layout={MainLayout}
                                    breadcrumbTitle="Budgets"
                                    title={<Breadcrumb parent="Budgets" parentPath="/budgets/all" child="Budget" />}
                                />
                            }
                        />
                    ))}

                    <Route path="/notes/payment/:id" element={<RedirectRoute to="/payments/pay/:id" />} />
                    <Route path="/notes/expense/:id" element={<RedirectRoute to="/expenses/open" />} />
                    <Route path="*" element={<NotFoundRoute />} />
                </Routes>
            </RootErrorBoundary>
        </Router>
    );
};

export default App;

// constantly calculate app height so that 100vh works on webkit. Needed for mobile safari, ipad etc.
const appHeight = () => {
    const doc = document.documentElement;
    doc.style.setProperty('--vh', `${window.innerHeight}px`);
};

window.addEventListener('resize', appHeight);
appHeight();
